/**
 * Gestione dei dipendenti dello studio da parte del titolare (`/api/associate/dipendenti`).
 * Area riservata: axios con il token dell'associato, come `otpAuthService`.
 */

import axios from 'axios';
import { getAuthHeader } from './otpAuthService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const BASE = `${API_BASE_URL}/associate/dipendenti`;

export interface DipendenteStudio {
  id: string;
  nome: string;
  cognome: string;
  sezioni: string[];
  stato: 'attivo' | 'sospeso';
  lastLoginAt?: string | null;
  associate?: string;
  // sede dello studio a cui è assegnato (solo organizzativa): id null = sede principale
  sede?: { id?: string | null; nome?: string | null } | null;
  // false: dipendente di un'altra sede con la stessa P.IVA, in sola lettura
  modificabile?: boolean;
}

/** Sede selezionabile dello studio: `id: null` è la sede principale. */
export interface SedeStudio {
  id: string | null;
  nome: string;
}

export interface ElencoDipendenti {
  dipendenti: DipendenteStudio[];
  totale: number;
  // solo nella risposta admin: agli associati il limite non viene mai mostrato
  limite?: number;
  sedi?: SedeStudio[];
  sezioniDisponibili: string[];
  impostazioni: { sondaggiRispondonoDipendenti: boolean };
}

/** Credenziali mostrate una volta sola dopo la creazione o il reset della password. */
export interface CredenzialiDipendente {
  partitaIVA: string;
  nominativo: string;
  password: string;
  // testo pronto da copiare (P.IVA, nome e cognome, password, link al sito)
  messaggio: string;
}

export interface ImpostazioniDipendenti {
  sondaggiRispondonoDipendenti: boolean;
}

/** Motivi del 400 sulla password (`validatePassword` del server, stessa policy dello studio). */
export const MOTIVI_PASSWORD: Record<string, string> = {
  too_short: 'La password deve avere almeno 8 caratteri',
  too_obvious: 'Password troppo prevedibile: evita il nome, quello dello studio o la Partita IVA',
  common: 'Password troppo comune: scegline una meno facile da indovinare',
  uguale_titolare: 'La password del dipendente deve essere diversa da quella dello studio',
};

/**
 * Testo dell'errore sulla password: `uguale_titolare` sempre con il testo dedicato; per gli altri
 * motivi il messaggio del server e, se manca, il testo della policy.
 */
export const testoErrorePassword = (reason?: string | null, message?: string | null): string | undefined => {
  if (reason === 'uguale_titolare') return MOTIVI_PASSWORD.uguale_titolare;
  return message || (reason ? MOTIVI_PASSWORD[reason] : undefined) || undefined;
};

/** Errore del server con stato HTTP e codice (`DIPENDENTI_SPENTI`, `LIMITE_DIPENDENTI`, ...). */
export class ErroreDipendenti extends Error {
  status?: number;
  code?: string;
  reason?: string;

  constructor(message: string, info: { status?: number; code?: string; reason?: string } = {}) {
    super(message);
    this.name = 'ErroreDipendenti';
    this.status = info.status;
    this.code = info.code;
    this.reason = info.reason;
  }
}

const intestazioni = () => ({ headers: getAuthHeader() as any });

const traduciErrore = (error: any): ErroreDipendenti => {
  const risposta = error?.response;
  if (!risposta) return new ErroreDipendenti('Errore di connessione al server');
  const corpo = risposta.data || {};
  const messaggio = testoErrorePassword(corpo.reason, corpo.message) || 'Operazione non riuscita';
  return new ErroreDipendenti(messaggio, { status: risposta.status, code: corpo.code, reason: corpo.reason });
};

const chiama = async <T>(richiesta: () => Promise<{ data: any }>): Promise<T> => {
  try {
    const res = await richiesta();
    return res.data as T;
  } catch (error) {
    throw traduciErrore(error);
  }
};

export const getDipendenti = (): Promise<ElencoDipendenti> =>
  chiama(() => axios.get(BASE, intestazioni()));

export const creaDipendente = (body: { nome: string; cognome: string; password: string; sezioni?: string[]; sede?: string | null }) =>
  chiama<{ dipendente: DipendenteStudio; credenziali: CredenzialiDipendente }>(() => axios.post(BASE, body, intestazioni()));

export const aggiornaSezioniDipendente = (id: string, sezioni: string[]) =>
  chiama<unknown>(() => axios.put(`${BASE}/${encodeURIComponent(id)}/sezioni`, { sezioni }, intestazioni()));

export const cambiaSedeDipendente = (id: string, sede: string | null) =>
  chiama<{ dipendente?: DipendenteStudio }>(() => axios.put(`${BASE}/${encodeURIComponent(id)}/sede`, { sede }, intestazioni()));

export const reimpostaPasswordDipendente = (id: string, password: string) =>
  chiama<{ dipendente?: DipendenteStudio; credenziali: CredenzialiDipendente }>(
    () => axios.post(`${BASE}/${encodeURIComponent(id)}/password`, { password }, intestazioni())
  );

export const sospendiDipendente = (id: string) =>
  chiama<unknown>(() => axios.post(`${BASE}/${encodeURIComponent(id)}/sospendi`, {}, intestazioni()));

export const riattivaDipendente = (id: string) =>
  chiama<unknown>(() => axios.post(`${BASE}/${encodeURIComponent(id)}/riattiva`, {}, intestazioni()));

export const eliminaDipendente = (id: string) =>
  chiama<unknown>(() => axios.delete(`${BASE}/${encodeURIComponent(id)}`, intestazioni()));

export const salvaImpostazioniDipendenti = (impostazioni: ImpostazioniDipendenti) =>
  chiama<unknown>(() => axios.put(`${BASE}/impostazioni`, impostazioni, intestazioni()));
